// Composable for authorization functionality
import { computed } from 'vue'
import { useStore } from 'vuex'

export function useAuthorization () {
  const store = useStore()

  // Computed properties for reactive authorization state
  const userRole = computed(() => store.getters['authorization/userRole'])
  const permissions = computed(() => store.state.authorization.permissions)
  const organizationId = computed(() => store.state.authorization.organizationId)
  const isInitialized = computed(() => store.state.authorization.initialized)

  // Role checks
  const isAdmin = computed(() => store.getters['authorization/isAdmin'])
  const isOrgAdmin = computed(() => store.getters['authorization/isOrgAdmin'])
  const isOrgMember = computed(() => store.getters['authorization/isOrgMember'])

  // Permission checking functions
  const hasPermission = (permission) => {
    return store.getters['authorization/hasPermission'](permission)
  }

  const hasRole = (role) => {
    return store.getters['authorization/hasRole'](role)
  }

  const hasAnyRole = (roles) => {
    return roles.some(role => hasRole(role))
  }

  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => hasPermission(permission))
  }

  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission))
  }

  // Resource access checking
  const canAccessResource = async (resourceOwnerId, resourceOrgId = null) => {
    return await store.dispatch('authorization/checkResourceAccess', {
      resourceOwnerId,
      organizationId: resourceOrgId
    })
  }

  // Permission checking with server validation
  const checkPermission = async (permission) => {
    return await store.dispatch('authorization/checkPermission', permission)
  }

  // Initialize authorization if not already done
  const initializeAuthorization = async () => {
    if (!isInitialized.value) {
      return await store.dispatch('authorization/initializeAuthorization')
    }
    return true
  }

  // Common permission helpers
  const canManageUsers = computed(() => hasPermission('manage_all_users') || hasPermission('manage_org_users'))
  const canReadAllResources = computed(() => hasPermission('read_all_resources'))
  const canWriteAllResources = computed(() => hasPermission('write_all_resources'))
  const canManageSystem = computed(() => hasPermission('manage_system'))

  // Navigation helpers
  const canAccessUsersPage = computed(() => hasPermission('manage_all_users') || hasPermission('manage_org_users'))
  const canAccessOrganizationsPage = computed(() => hasAnyRole(['bits_admin', 'superadmin', 'customer_owner']))
  const canAccessSettingsPage = computed(() => hasPermission('manage_system'))
  const canAccessCamera = computed(() => hasPermission('access_camera'))
  const canAccessRequests = computed(() => hasPermission('access_requests'))

  return {
    // State
    userRole,
    permissions,
    organizationId,
    isInitialized,

    // Role checks
    isAdmin,
    isOrgAdmin,
    isOrgMember,

    // Permission functions
    hasPermission,
    hasRole,
    hasAnyRole,
    hasAllPermissions,
    hasAnyPermission,

    // Resource access
    canAccessResource,
    checkPermission,

    // Initialization
    initializeAuthorization,

    // Common helpers
    canManageUsers,
    canReadAllResources,
    canWriteAllResources,
    canManageSystem,

    // Navigation helpers
    canAccessUsersPage,
    canAccessOrganizationsPage,
    canAccessSettingsPage,
    canAccessCamera,
    canAccessRequests
  }
}
